import { supabase, isSupabaseConfigured } from './supabaseClient';

const EMPTY_STATE = Object.freeze({ subscriptions: [], deliveries: [] });
let cachedState = EMPTY_STATE;
let cacheOwnerId = null;
let cacheGeneration = 0;
let stateRequest = null;
let stateLoaded = false;

const authenticatedSession = async () => {
  if (!isSupabaseConfigured) throw new Error('Sign-in is not configured.');
  const { data: { session } = {}, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!session?.access_token || !session?.user?.id) throw new Error('Please sign in to manage verse delivery.');
  return { accessToken: session.access_token, userId: session.user.id };
};

const apiRequest = async (path, options, auth) => {
  let response;
  try {
    response = await fetch(path, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.accessToken}`,
        ...(options.headers || {})
      }
    });
  } catch (_error) {
    throw new Error('Verse Delivery could not reach the server. Please try again.');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Verse Delivery could not save that change.');
  return payload;
};

const rememberState = (payload, userId, generation) => {
  if (generation !== cacheGeneration) return EMPTY_STATE;
  cachedState = {
    subscriptions: Array.isArray(payload?.subscriptions) ? payload.subscriptions : [],
    deliveries: Array.isArray(payload?.deliveries) ? payload.deliveries : []
  };
  cacheOwnerId = userId;
  stateLoaded = true;
  return cachedState;
};

const rejectIfAccountChanged = (userId, generation) => {
  if (generation !== cacheGeneration || (cacheOwnerId && cacheOwnerId !== userId)) {
    throw new Error('Your signed-in account changed before that request finished. Please try again.');
  }
};

export const clearDeliveryStateCache = () => {
  cacheGeneration += 1;
  cachedState = EMPTY_STATE;
  cacheOwnerId = null;
  stateRequest = null;
  stateLoaded = false;
};

export const loadDeliveryState = async ({ force = false } = {}) => {
  const auth = await authenticatedSession();
  if ((cacheOwnerId && cacheOwnerId !== auth.userId)
    || (stateRequest && stateRequest.userId !== auth.userId)) {
    clearDeliveryStateCache();
  }
  if (force && stateRequest) clearDeliveryStateCache();
  if (!force && stateRequest?.userId === auth.userId) return stateRequest.promise;
  if (!force && stateLoaded && cacheOwnerId === auth.userId) return cachedState;

  const generation = cacheGeneration;
  const request = apiRequest('/api/subscriptions', { method: 'GET' }, auth)
    .then((payload) => rememberState(payload, auth.userId, generation))
    .finally(() => {
      if (stateRequest?.promise === request) stateRequest = null;
    });
  stateRequest = { userId: auth.userId, generation, promise: request };
  return request;
};

export const getCachedDeliveryState = () => cachedState;

export const getSubscriptions = async (options) => (await loadDeliveryState(options)).subscriptions;
export const getDeliveries = async (options) => (await loadDeliveryState(options)).deliveries;

export const createSubscription = async (form) => {
  const auth = await authenticatedSession();
  const generation = cacheGeneration;
  const payload = await apiRequest('/api/subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      topic: form.topic,
      contentType: form.contentType,
      cadence: form.cadence,
      customTime: form.customTime || null,
      timezone: form.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      medium: form.medium,
      destination: form.destination
    })
  }, auth);
  rejectIfAccountChanged(auth.userId, generation);
  await loadDeliveryState({ force: true });
  return payload;
};

const patchState = async (resource, id, changes) => {
  const auth = await authenticatedSession();
  const generation = cacheGeneration;
  const payload = await apiRequest('/api/subscriptions', {
    method: 'PATCH',
    body: JSON.stringify({ resource, id, changes })
  }, auth);
  rejectIfAccountChanged(auth.userId, generation);
  return rememberState(payload, auth.userId, generation);
};

export const updateDelivery = (deliveryId, changes) => patchState('delivery', deliveryId, changes);
export const updateSubscription = (subscriptionId, changes) => patchState('subscription', subscriptionId, changes);

export const pauseSubscription = (subscriptionId, currentlyPaused = false) => (
  updateSubscription(subscriptionId, { status: currentlyPaused ? 'active' : 'paused' })
);

export const sendTestDelivery = async (subscription, delivery) => {
  const auth = await authenticatedSession();
  const generation = cacheGeneration;
  const payload = await apiRequest('/api/deliveries/send', {
    method: 'POST',
    body: JSON.stringify({
      subscriptionId: subscription.id,
      deliveryId: delivery.id,
      test: true
    })
  }, auth);
  rejectIfAccountChanged(auth.userId, generation);
  await loadDeliveryState({ force: true });
  return payload;
};

export default {
  loadDeliveryState,
  getSubscriptions,
  getDeliveries,
  createSubscription,
  updateDelivery,
  updateSubscription,
  pauseSubscription,
  sendTestDelivery,
  clearDeliveryStateCache
};
